import React, {useContext} from 'react';
import {StyleSheet, Dimensions, View} from 'react-native';
import Styles from '../../Styles';
import AVText from 'src/components/elements/AVText';
import Localized from 'src/constants/AppStrings';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import {useAppSelector} from 'src/redux/hooks';
import {store} from 'src/redux/store';
import NavActions from 'src/actions/NavActions';
import {
  fetchRecentCampusesAndLocations,
  swicthCurrentCampusesAndLocations,
} from 'src/redux/slices/campusLocationSlice';
import Events from 'src/logging/Events';
import ScreenContext from 'src/components/ScreenContext';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import {getDescriber} from 'src/components/elements/home/descriptor/DescriptorType';
import PlatformApi from 'src/api/PlatformApi';
import Util from 'src/Util';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';

export type NearbyLocationType = {
  locationId?: string;
  campusId?: string;
  displayName: string;
  distance: number;
  units?: string;
};

export enum LocationDistanceUnit {
  IMPERIAL = 'IMPERIAL',
  METRIC = 'METRIC',
}

type NearbyLocationViewProps = {
  nearbyLocation: NearbyLocationType;
  mismatchLocationBalance: (value: boolean) => void;
  isOnlyTile: boolean;
};

const width = Dimensions.get('window').width;

const NewNearbyLocationView = (props: NearbyLocationViewProps) => {
  const isHomeLocationSma = useAppSelector((s) => s.account.isHomeLocationSma);
  const homeOrgId = useAppSelector((s) => s.account.account.orgId);
  const accountId = useAppSelector((s) => s.account.account.id);
  const context = useContext(ScreenContext);

  //Select Nearby Location/Campus and fetch all the locations in the selected campus
  const onClickNearbyLocation = async () => {
    try {
      context.actions.showSpinner();
      const nearbyLocation = await PlatformApi.fetchLocation(
        props.nearbyLocation.locationId,
      );
      const nearbyLocationsList = await Util.filterLocationsPerBalanceType(
        isHomeLocationSma,
        homeOrgId,
        [nearbyLocation],
      );
      if (nearbyLocationsList.unSupportedLocations.length > 0) {
        props.mismatchLocationBalance(true);
        return;
      } else if (nearbyLocationsList.supportedLocations.length > 0) {
        await store.dispatch(
          swicthCurrentCampusesAndLocations({
            accountId,
            locationId: props.nearbyLocation.locationId,
          }),
        );
        await store.dispatch(fetchRecentCampusesAndLocations(accountId));
        NavActions.pop();
        FirebaseAnalytic.trackEvent(
          'onClickNearbyLocation',
          'NearbyLocationView',
          {
            ...props,
          },
        );
      }
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'NearbyLocationView:onClickNearbyLocation',
        generateErrorMessage(error),
      );
      Events.Error.trackEvent(
        'Exception',
        'NearbyLocationView:onClickNearbyLocation',
        generateErrorMessage(error),
      );
    } finally {
      context.actions.hideSpinner();
    }
  };

  return (
    <View
      accessible={true}
      accessibilityRole="button"
      accessibilityLabel={`Location${props.nearbyLocation.displayName}`}
      aria-label={`Location${props.nearbyLocation.displayName}`}
      accessibilityHint={`Double tap to select ${props.nearbyLocation.displayName}`}
      style={[
        styles.container,
        !props.isOnlyTile && {width: styles.container.width / 2},
      ]}
    >
      <View style={styles.textView}>
        <AVText
          accessibilityLabel={`LocationName${props.nearbyLocation.displayName}`}
          accessibilityRole="text"
          aria-label={`${props.nearbyLocation.displayName}, text`}
          numberOfLines={2}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
          style={getDescriber().getCampusLocationStyles().locationDisplatText}
        >
          {props.nearbyLocation.displayName}
        </AVText>
        <AVText
          numberOfLines={1}
          accessibilityLabel={props.nearbyLocation?.distance.toFixed(1)}
          aria-label={`${props.nearbyLocation?.distance.toFixed(1)}, text`}
          accessibilityRole="text"
          accessible={true}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm1}
          style={[
            styles.distanceText,
            getDescriber().getCampusLocationStyles().locationDisplatSubText,
          ]}
        >
          {props.nearbyLocation?.distance.toFixed(1) +
            ' ' +
            Localized.Labels.miles}
        </AVText>
        <View style={styles.btnContentView}>
          <RoundedAppButton
            buttonViewStyle={styles.btnViewStyle}
            buttonContainerStyle={styles.btnContainerStyle}
            textStyle={styles.btnText}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
            buttonType={RoundedAppButtonType.Solid}
            titleText={Localized.Labels.select}
            accessibilityLabelValue={Localized.Labels.select}
            aria-label={Localized.Labels.select}
            onPress={onClickNearbyLocation}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: width - Styles.Spacing.m3 * 2,
    margin: Styles.Spacing.m2,
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    borderRadius: Styles.Spacing.m2,
    ...Styles.Style.shadow,
  },
  textView: {
    flex: 1,
    justifyContent: 'center',
    paddingVertical: Styles.Spacing.m3,
    marginHorizontal: Styles.Spacing.m2 + Styles.Spacing.m1,
  },
  distanceText: {
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
    width: Styles.Heights.h6 * 2,
    textAlignVertical: 'bottom',
    height: Styles.Spacing.m3 + Styles.Spacing.m1,
    marginTop: Styles.Spacing.m3,
  },
  btnContentView: {
    flex: 1,
    justifyContent: 'center',
    paddingVertical: Styles.Spacing.m2,
  },
  btnViewStyle: {
    width: '100%',
    height: Styles.Heights.touchTargetHeight1 - Styles.Spacing.m1,
  },
  btnContainerStyle: {
    width: '100%',
  },
  btnText: {
    fontSize: Styles.Fonts.f8,
    fontWeight: '700',
  },
});

export default NewNearbyLocationView;
